/**
 * Authentication type definitions for AI-Tutor application
 * Aligned with AWS Cognito authentication flow
 */

export interface User {
  userId: string;
  email: string;
  name?: string;
  emailVerified?: boolean;
  // US-003 T1: cognito groups from /auth/login (TS-001); ['parent'] for parents, absent/[] for students.
  // Optional so existing persisted users in localStorage without a groups key do not break hydration.
  groups?: string[];
  // google-sign-in/US-001 (T3/T4, AC-001.3): 'google' for parents who signed in via
  // "Continue with Google" (Cognito Hosted-UI federation). Absent/undefined for
  // password-login users. useAuth's refresh mutation branches on this flag to route
  // federated sessions through Amplify's own token refresh (getFreshFederatedTokens)
  // instead of POST /auth/refresh, since Amplify v6 never surfaces a raw Cognito
  // refresh token for federated users. Mirrors the /users/me response contract's
  // optional `channel` field (interface-contracts-consolidated.json API-002).
  channel?: 'google';
  // BUG-20260713-020205 (D2, Option B) / T-8: mirrors the /users/me response's
  // optional `consentPending` field (types/pebbles.ts UserProfile). Threaded
  // through oauthReconciler.fetchParentProfile so OAuthCallbackPage can decide
  // whether a login-intent Google user is not-yet-registered (consentPending
  // true) and must be bounced to /register. Absent/undefined for
  // password-login users and any profile response that omits the field.
  consentPending?: boolean;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  idToken: string;
  expiresIn: number; // seconds until expiration
}

export interface LoginRequest {
  /**
   * Username OR email address — the pool sets AliasAttributes:[email], so Cognito
   * accepts either. Students hold placeholder emails that cannot receive mail, so
   * this can never narrow to email-only.
   */
  username: string;
  password: string;
  /**
   * Role-split sign-in (2026-07-29): which door the user chose. OPTIONAL on purpose —
   * omitted means no check, so every existing caller keeps working and the backend gate
   * ships inert until this field starts being sent. The backend blocks only on a positive
   * contradiction (a student account at the parent door and vice versa); an account with
   * no groups is allowed through, so a future group-assignment regression degrades to
   * "wrong dashboard" rather than "cannot sign in".
   *
   * NOT a security boundary — the Cognito client permits direct USER_PASSWORD_AUTH and its
   * ID ships in this bundle, so anyone with valid credentials can obtain tokens without
   * passing through here. Real authorization stays per-endpoint. Do not build on this.
   */
  role?: 'parent' | 'student';
}

export interface RegisterRequest {
  email: string;
  password: string;
  // TS-015 / T1: renamed from `inviteCode` -> `referralCode` (was inviteCode; renamed under
  // TS-015). Optional referral code; never blocks signup (FR-1.2). Carried verbatim to the
  // backend (authApi.register posts this object as-is), which maps it onto the legacy
  // `custom:invite_code` carrier (DD-004). Enforces TO-LL-003 (contract lockstep with API-005)
  // and TO-LL-044 (camelCase for this JS-origin field).
  referralCode?: string;
  // Required by /auth/register contract: the single combined consent flag (one affirmative
  // checkbox covering both the Terms of Service and the Privacy Policy). Missing/false causes
  // 400. The client sends NO version — the consent version is server-authoritative and stamped
  // by sync_user.py into two independently-versioned records (BUG-20260630-114135).
  tncAccepted: boolean;
}

export interface LoginResponse {
  user: User;
  tokens: AuthTokens;
}

export interface RegisterResponse {
  userId: string;
  message: string;
  /**
   * Signup simplification (2026-07-29): the server-generated Cognito username
   * (`parent_<random>`). The parent never sees or types it — it exists only so the
   * verify step can confirm without a lookup.
   *
   * WHY THIS FIELD MATTERS: the pool uses email as an ALIAS, and an alias is not active
   * until the account is confirmed. So between register and verify the email is not yet a
   * usable identifier and only the literal username works. Forwarding it to the verify
   * route is the fast path. If it is ever lost, VerifyEmailPage falls back to asking for
   * the email — see that page for the recovery path.
   */
  username: string;
  /**
   * True when register found an existing UNCONFIRMED account for this email and resent its
   * code instead of creating a second one. Drives the "we've sent you a fresh code" banner.
   * A flag rather than sniffing `message`, so the copy can change freely.
   */
  codeResent?: boolean;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
  idToken: string;
  expiresIn: number;
}

/**
 * Signup simplification (2026-07-29): identify by EITHER field, exactly one required.
 *
 *  - `username` — the fast path, forwarded from RegisterResponse. Used verbatim by the
 *    backend, so it does no lookup and is immune to lookup lag right after signup.
 *  - `email` — the recovery path, for when the username was never held or has been lost
 *    (refresh, new tab, another device). The backend resolves it to the account.
 *
 * Both optional in the type because either alone is valid; sending neither is a 400.
 * Keeping `username` accepted is also what keeps pre-existing accounts working — parents
 * who are mid-signup right now chose their own usernames, which nothing can derive.
 */
export interface VerifyEmailRequest {
  username?: string;
  email?: string;
  code: string;
}

export interface VerifyEmailResponse {
  message: string;
}

/** Either-field, same contract and same reasoning as VerifyEmailRequest. */
export interface ResendCodeRequest {
  username?: string;
  email?: string;
}

export interface ResendCodeResponse {
  message: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  message: string;
}

export interface ResetPasswordRequest {
  email: string;
  code: string;
  newPassword: string;
}

export interface ResetPasswordResponse {
  message: string;
}

export interface AuthError {
  code: string;
  message: string;
  /**
   * The ONLY error payload beyond code/message that survives the API layer —
   * `lib/api.ts` builds AuthError from exactly `code`, `message` and `details`, so any
   * other top-level field on an error body is discarded before a caller sees it. New
   * error data must be nested here.
   *
   * Known key: `existingProvider` ('google' | 'password') on a 409 EMAIL_EXISTS, which
   * tells RegisterPage which recovery CTA to show. Offering "Continue with Google" to
   * someone whose account has a password is a dead end, so the two cases must not share
   * one CTA.
   */
  details?: Record<string, string>;
}

// Error codes matching backend API
export const AUTH_ERROR_CODES = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  CONFLICT: 'CONFLICT',
  RATE_LIMITED: 'RATE_LIMITED',
  SERVER_ERROR: 'SERVER_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  SIGNUP_CAP_REACHED: 'SIGNUP_CAP_REACHED',
  INVALID_INVITE_CODE: 'INVALID_INVITE_CODE',
  // BUG-20260225-155434: Added EMAIL_EXISTS for duplicate email registration error - 2026-02-25
  EMAIL_EXISTS: 'EMAIL_EXISTS',
  // US-021 T1: 402 — family wallet empty; student sees kid-friendly 'ask a grown-up' state (CCM-03).
  // NEVER show coin counts, prices, or 'top up' language to the student — only the error CODE is used.
  INSUFFICIENT_COINS: 'INSUFFICIENT_COINS',
} as const;

export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[keyof typeof AUTH_ERROR_CODES];
